import { ComponentMaintenanceDataWithDone, ComponentWithReplacement, GroupedComponents, MaintenanceDraftWithDone } from '../types';
import { Building } from '../../../../types/protocols';
import { ComponentLifetime, TemplatesByType } from '../../TemplatesEditor/types';

/**
 * Validate year value is within acceptable range
 */
export const isValidYear = (year: number): boolean => {
    const currentYear = new Date().getFullYear();
    const maxYear = currentYear + 150;
    return year >= currentYear && year <= maxYear;
};

/**
 * Validate interval value is within acceptable range
 */
export const isValidInterval = (interval: number): boolean => {
    return interval > 0 && interval <= 3650;
};

/**
 * Check if a replacement year field is invalid
 */
export const isReplacementYearInvalid = (
    componentKey: string,
    draftComponents: { [key: string]: ComponentMaintenanceDataWithDone }
): boolean => {
    const replacementYear = draftComponents[componentKey]?.replacementYear;
    return replacementYear !== undefined && !isValidYear(replacementYear);
};

/**
 * Check if a maintenance date field is invalid
 */
export const isMaintenanceDateInvalid = (
    componentKey: string,
    templateId: string,
    draftComponents: { [key: string]: ComponentMaintenanceDataWithDone }
): boolean => {
    const activity = draftComponents[componentKey]?.maintenanceActivities?.[templateId];
    const nextDate = activity?.nextDate;
    if (nextDate) {
        try {
            const date = new Date(nextDate);
            return isNaN(date.getTime());
        } catch {
            return true;
        }
    }
    return false;
};

/**
 * Check if a maintenance interval field is invalid
 */
export const isMaintenanceIntervalInvalid = (
    componentKey: string,
    templateId: string,
    draftComponents: { [key: string]: ComponentMaintenanceDataWithDone }
): boolean => {
    const activity = draftComponents[componentKey]?.maintenanceActivities?.[templateId];
    return activity?.intervalValue !== undefined && !isValidInterval(activity.intervalValue);
};

/**
 * Check if component has valid saved values
 */
export const hasValidSavedValues = (
    componentKey: string,
    draftComponents: { [key: string]: ComponentMaintenanceDataWithDone }
): boolean => {
    const componentData = draftComponents[componentKey];
    if (!componentData) return false;

    // Check replacement year
    if (componentData.replacementYear && !isValidYear(componentData.replacementYear)) {
        return false;
    }

    // Check maintenance years
    if (componentData.maintenanceActivities) {
        for (const activity of Object.values(componentData.maintenanceActivities)) {
            if (activity.nextDate && !isValidYear(new Date(activity.nextDate).getFullYear())) {
                return false;
            }
        }
    }

    return true;
};


/**
 * Count done components in a list
 */
export const countDoneComponents = (
    components: ComponentWithReplacement[],
    draftComponents: { [key: string]: ComponentMaintenanceDataWithDone }
): number => {
    return components.filter(plan => {
        const componentKey = plan.component.key;
        return draftComponents[componentKey]?.isDone === true;
    }).length;
};

/**
 * Validate that all necessary data is fully loaded
 */
export const validateDataLoaded = (
    building: Building | null,
    componentLifetimes: Record<string, ComponentLifetime>,
    maintenanceTemplates: TemplatesByType,
    maintenanceDraft: MaintenanceDraftWithDone,
    groupedComponents: GroupedComponents,
    loading: boolean
): boolean => {
    const hasBuilding = !!(building && building.model && building.model.structure && building.model.structure.components);
    const hasComponentLifetimes = Object.keys(componentLifetimes).length > 0;
    // Maintenance templates are optional - not all component types have maintenance activities
    // They're only needed for periodic maintenance, not for replacement planning
    const hasDraftData = !!(maintenanceDraft && maintenanceDraft.components !== undefined);

    // Check if building has components - if it does, we need processed components too
    const buildingComponents = building?.model?.structure?.components;
    const buildingHasComponents = buildingComponents && Object.keys(buildingComponents).length > 0;
    const hasProcessedComponents = Object.keys(groupedComponents).length > 0;

    // If building has components, we need them to be processed. If not, we just need the other data.
    const componentsValid = !buildingHasComponents || hasProcessedComponents;

    // Note: Removed hasMaintenanceTemplates check as templates are optional
    return hasBuilding && hasComponentLifetimes && hasDraftData && componentsValid && !loading;
};
